/**
 * Funnel telemetry — thin named helpers around logAnalyticsEvent.
 *
 * All helpers are non-blocking and best-effort. They MUST NEVER throw,
 * block rendering, or surface errors to the user.
 *
 * Events instrumented (2026-06):
 *   - page_view        (every route change)
 *   - search_performed (BrowseCases / search submissions)
 *   - case_viewed      (CaseDetail successful load)
 *   - bookmark_added   (SaveCaseButton save success)
 *   - case_followed    (Follow API — server-side as well)
 *   - pricing_viewed   (PricingPage mount)
 *   - checkout_started (Stripe checkout before redirect)
 *   - paywall_hit      (MatchesPaywall mount, save-limit modal, etc.)
 */
import { logAnalyticsEvent } from '@/lib/referral';

export function track(eventName, properties = {}) {
  // Fire and forget. logAnalyticsEvent already swallows errors.
  logAnalyticsEvent(eventName, properties);
}

export function trackPageView(path, extra = {}) {
  track('page_view', { path, ...extra });
}

export function trackSearchPerformed(query, resultCount, mode = 'standard') {
  track('search_performed', {
    query_length: String(query || '').length,
    has_query: Boolean(query && String(query).trim()),
    result_count: typeof resultCount === 'number' ? resultCount : null,
    mode,
  });
}

export function trackCaseViewed(caseId, props = {}) {
  track('case_viewed', { case_id: caseId, ...props });
}

export function trackBookmarkAdded(caseId, props = {}) {
  track('bookmark_added', { case_id: caseId, ...props });
}

export function trackBookmarkRemoved(caseId) {
  track('bookmark_removed', { case_id: caseId });
}

export function trackCaseFollowed(caseId) {
  track('case_followed', { case_id: caseId });
}

export function trackPricingViewed(props = {}) {
  track('pricing_viewed', props);
}

export function trackCheckoutStarted(planId, billingPeriod = 'monthly', extra = {}) {
  track('checkout_started', { plan_id: planId, billing_period: billingPeriod, ...extra });
}

export function trackPaywallHit(surface, props = {}) {
  // surface: 'matches' | 'save_limit' | 'feature_lock' | etc.
  track('paywall_hit', { surface, ...props });
}
